import React, { useEffect, useState } from 'react';
import {
    Card,
    CardContent,
    Typography,
    CircularProgress,
    Box,
    Grid,
    Avatar,
    Alert,
    Skeleton
} from '@mui/material';
import { blue, green } from '@mui/material/colors';
import HubIcon from '@mui/icons-material/Hub';
import GroupIcon from '@mui/icons-material/Group';
import { useTranslation } from 'react-i18next';

import { API_BASE_URL } from '../../utils/constants';

const UserConnections = ({ userId }) => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        setLoading(true);
        setError(null);
        fetch(`${ API_BASE_URL }/explorer/user/${ userId }/connection`)
            .then(res => res.json())
            .then(json => {
                setData(json);
                setLoading(false);
            })
            .catch(err => {
                setError(t('user.failedToFetchConnections'));
                setLoading(false);
            });
    }, [userId]);


    if (loading) {
        return (
            <Card sx={{ p: { xs: 2, sm: 3 }, width: '100%', boxShadow: 3 }}>
                <CardContent>
                    <Skeleton variant="text" width="60%" height={32} sx={{ mb: 2 }} />
                    <Grid container spacing={3} mt={2}>
                        {/* Skeleton for Associated Addresses */}
                        <Grid item xs={12} sm={6}>
                            <Card sx={{ height: '100%' }}>
                                <CardContent sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                                    <Skeleton variant="circular" width={56} height={56} />
                                    <Box sx={{ flex: 1 }}>
                                        <Skeleton variant="text" width="80%" height={20} sx={{ mb: 1 }} />
                                        <Skeleton variant="text" width="40%" height={36} />
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>

                        {/* Skeleton for Associated Users */}
                        <Grid item xs={12} sm={6}>
                            <Card sx={{ height: '100%' }}>
                                <CardContent sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                                    <Skeleton variant="circular" width={56} height={56} />
                                    <Box sx={{ flex: 1 }}>
                                        <Skeleton variant="text" width="80%" height={20} sx={{ mb: 1 }} />
                                        <Skeleton variant="text" width="40%" height={36} />
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>
        );
    }

    if (error) {
        return (
            <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" sx={{ p: 4, minHeight: 250, bgcolor: 'error.lighter', borderRadius: 2 }}>
                <Typography color="error" fontWeight="bold">{t('common.error')}</Typography>
                <Typography sx={{ mt: 1, textAlign: 'center' }}>{error}</Typography>
            </Box>
        );
    }

    if (!data) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" sx={{ p: 4, minHeight: 250 }}>
                <Typography color="text.secondary">{t('user.noConnectionData')}</Typography>
            </Box>
        );
    }

    return (
        <Card sx={{ p: { xs: 2, sm: 3 }, width: '100%', boxShadow: 3 }}>
            <CardContent>
                <Typography variant="h6" component="h4" gutterBottom fontWeight="bold">
                    {t('user.possibleConnections')}
                </Typography>
                <Grid container spacing={3} mt={2}>
                    {/* Stat Card for Associated Addresses */}
                    <Grid item xs={12} sm={6}>
                        <Card sx={{ height: '100%' }}>
                            <CardContent sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                                <Avatar sx={{ bgcolor: blue[100], color: 'primary.dark', width: 56, height: 56 }}>
                                    <HubIcon fontSize="large" />
                                </Avatar>
                                <Box>
                                    <Typography variant="body1" color="text.secondary">
                                        {t('user.associatedAddresses')}
                                    </Typography>
                                    <Typography variant="h4" component="p" fontWeight="bold">
                                        {data.associatedAddressCount}
                                    </Typography>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Stat Card for Associated Users */}
                    <Grid item xs={12} sm={6}>
                        <Card sx={{ height: '100%' }}>
                            <CardContent sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                                <Avatar sx={{ bgcolor: green[100], color: 'success.dark', width: 56, height: 56 }}>
                                    <GroupIcon fontSize="large" />
                                </Avatar>
                                <Box>
                                    <Typography variant="body1" color="text.secondary">
                                        {t('user.associatedUsers')}
                                    </Typography>
                                    <Typography variant="h4" component="p" fontWeight="bold">
                                        {data.associatedUserCount}
                                    </Typography>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>

                {/* Disclaimer for high user count */}
                {data.associatedUserCount > 500 ? (
                    <Alert
                        severity="info"
                        sx={{ mt: 3 }}
                    >
                        <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                            {t('user.highConnectionCount')}
                        </Typography>
                        <Typography variant="body2">
                            {t('user.centralizedExchangeWarning')}
                        </Typography>
                    </Alert>
                ) : (
                    <Alert
                        severity="info"
                        sx={{ mt: 3 }}
                    >
                        <Typography variant="body2">
                            {t('user.connectionsRecommendation')}
                        </Typography>
                    </Alert>
                )}
            </CardContent>
        </Card>
    );
};

export default UserConnections;
